/**
 * Tâche 1287 (Eric 2026-06-30) — Surlignage du bouton cible après navigation.
 *
 * La palette d'actions (Ctrl+K) navigue avec `?act=<data-testid>`. Ce hook
 * attend que le bouton existe, le fait défiler à l'écran, puis lui pose un
 * anneau PERMANENT : il ne disparaît qu'au prochain clic de l'utilisateur
 * (choix explicite de Vincent — l'anneau doit survivre au temps de lecture).
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const CLASS_ACTION_RING = "kn-action-ring";
const FLAG = "__kn_action_ring_css__";

function ensureCss() {
  if (typeof document === "undefined") return;
  if (document.documentElement.dataset[FLAG]) return;
  const style = document.createElement("style");
  style.setAttribute("data-kn-action-ring", "1");
  style.textContent = `
    .${CLASS_ACTION_RING} {
      outline: 3px solid #002FA7 !important;
      outline-offset: 4px;
      border-radius: 10px;
      box-shadow: 0 0 0 8px rgba(0, 47, 167, 0.18);
      position: relative;
      z-index: 5;
    }
  `;
  document.head.appendChild(style);
  document.documentElement.dataset[FLAG] = "1";
}

export function useActionHighlight() {
  const location = useLocation();
  useEffect(() => {
    ensureCss();
    const target = new URLSearchParams(location.search).get("act");
    if (!target) return;

    let cancelled = false;
    let elapsed = 0;
    let el = null;
    const clear = () => {
      if (el) el.classList.remove(CLASS_ACTION_RING);
      document.removeEventListener("click", onClick, true);
    };
    const onClick = () => clear();

    const tick = () => {
      if (cancelled) return;
      el = document.querySelector(`[data-testid="${CSS.escape(target)}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) { /* noop */ }
        el.classList.add(CLASS_ACTION_RING);
        // L'anneau reste jusqu'au prochain clic — y compris un clic sur le
        // bouton lui-même, qui vaut « j'ai compris, j'y vais ».
        setTimeout(() => document.addEventListener("click", onClick, true), 300);
        return;
      }
      elapsed += 250;
      if (elapsed >= 8000) return;
      setTimeout(tick, 250);
    };
    const initial = setTimeout(tick, 350);
    return () => { cancelled = true; clearTimeout(initial); clear(); };
  }, [location.search, location.pathname]);
}
